import { OrderedMap } from "immutable";
import {
  SecurityAppDetectionChains,
  SecurityAppSecurityEventNotifications,
  SecurityAppSecurityEvents
} from "@graylog/enterprise-api";
import { EventsDefinitions, EventsEntityTypes, EventsNotifications } from "@graylog/server-api";
import { qualifyUrl } from "util/URLUtils";
import EnterpriseApiRoutes from "common/ApiRoutes";
import fetch from "logic/rest/FetchProvider";
import { attributes } from "security-app/components/SecurityEvents/Alerts/Constants";
import { defaultTimeRange } from "components/events/fetchEvents";
import parseSecurityEventFilters from "security-app/components/SecurityEvents/Alerts/parseSecurityEventFilters";
import { slicesToFilters } from "components/common/PaginatedEntityTable/useFetchEntities";
export const fetchSecurityEventByIds = async (eventIds) => fetch("POST", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.detailsByIds().url), { event_ids: eventIds });
export const fetchSecurityEventsFilterOptions = async ({
  timerange,
  fields
}) => {
  const payload = { timerange, fields };
  return SecurityAppSecurityEvents.getFilterOptions(payload);
};
export const securityEventEntitiesKeyFn = (searchParams) => searchParams ? ["events", "search", searchParams] : ["events", "search"];
export const deserializeSecurityEventEntity = ({ event, ...rest }) => ({
  ...event,
  ...rest,
  id: rest.event_id
});
const deserializeSecurityEvents = ({
  security_events,
  page,
  per_page,
  grand_total,
  count
}) => ({
  attributes,
  list: security_events.map(deserializeSecurityEventEntity),
  pagination: { total: grand_total, page, per_page, count }
});
const mapSortBy = (attribute) => {
  if (!attribute) {
    return void 0;
  }
  switch (attribute) {
    case "scores":
      return "scores.normalized_risk";
    default:
      return attribute;
  }
};
export const parseSearchParams = (originalSearchParams, timezone) => {
  var _a, _b, _c;
  const searchParams = slicesToFilters(originalSearchParams);
  const omFilters = OrderedMap.isOrderedMap(searchParams.filters) ? searchParams.filters : OrderedMap(Object.entries(searchParams.filters));
  const parsedFilters = parseSecurityEventFilters(omFilters, defaultTimeRange);
  const { timerange, filter } = parsedFilters;
  const sortBy = mapSortBy((_a = searchParams.sort) == null ? void 0 : _a.attributeId);
  return {
    page: searchParams.page,
    per_page: searchParams.pageSize,
    sort_by: sortBy,
    sort_direction: (_b = searchParams.sort) == null ? void 0 : _b.direction,
    query: (_c = searchParams.query) != null ? _c : "",
    filter,
    timerange,
    timezone
  };
};
export const fetchPaginatedSecurityEventEntities = (searchParams, timezone = null) => SecurityAppSecurityEvents.search(parseSearchParams(searchParams, timezone)).then(deserializeSecurityEvents);
export const newSecurityEvent = async ({
  data
}) => fetch("POST", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.new().url), data);
export const updateSecurityEvent = async ({
  id,
  data
}) => fetch("PUT", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.singleEvent(id).url), data);
export const fetchUsersNTeams = async () => {
  const { users } = await SecurityAppSecurityEvents.getInvestigationUsers();
  const { teams } = await SecurityAppSecurityEvents.getInvestigationTeams();
  const normUsers = users.map((user) => ({ id: user.id, name: user.full_name, type: "User" }));
  const normTeams = teams.map((team) => ({ id: team.id, name: team.name, type: "Team" }));
  return Promise.resolve([...normUsers, ...normTeams]);
};
export const triggerSecurityEventNotification = async ({
  id,
  notificationId,
  stepId
}) => {
  await SecurityAppSecurityEventNotifications.executeForEvent(id, notificationId, stepId);
};
export const triggerBulkSecurityEventNotification = async ({
  ids,
  notificationId
}) => {
  await SecurityAppSecurityEventNotifications.executeForEvents({
    event_ids: ids,
    notification_id: notificationId
  });
};
export const fetchEventDefinitions = async (page, perPage, query = null, orderBy = "title", direction = "asc") => {
  const params = [`page=${page}`, `per_page=${perPage}`, `sort=${orderBy}`, `order=${direction}`];
  if (query) params.push(`query=${encodeURI(query)}`);
  return fetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.definitions.index(params.join("&")).url));
};
export const fetchEventDefinitionDetailsByIds = async (eventDefinitionIds) => fetch("POST", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.definitions.detailsByIds().url), {
  event_definition_ids: eventDefinitionIds
});
export const fetchEntityTypes = async () => EventsEntityTypes.all();
export const toggleEventDefinitionStatus = async ({ id, schedule }) => schedule ? EventsDefinitions.schedule(id) : EventsDefinitions.unschedule(id);
export const copyEventDefinition = async ({ id }) => EventsDefinitions.duplicate(id);
export const clearEventDefinitionNotificationsQueue = async ({ id }) => EventsDefinitions.clearNotificationQueue(id);
export const deleteEventDefinition = async ({ id }) => EventsDefinitions.remove(id);
export const blkEnableDefinition = async ({ entity_ids }) => EventsDefinitions.bulkSchedule({ entity_ids });
export const blkDisableDefinition = async ({ entity_ids }) => EventsDefinitions.bulkUnschedule({ entity_ids });
export const blkDeleteDefinition = async ({ entity_ids }) => EventsDefinitions.bulkDelete({ entity_ids });
export const fetchNotifications = async (page, perPage, query = null, orderBy = "title", direction = "asc") => {
  const params = [`page=${page}`, `per_page=${perPage}`, `sort=${orderBy}`, `order=${direction}`];
  if (query) params.push(`query=${encodeURI(query)}`);
  return fetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.securityNotifications.index(params.join("&")).url));
};
export const fetchNotificationDetails = async (notificationId) => fetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.securityNotifications.details(notificationId).url));
export const deleteNotification = async ({ id }) => EventsNotifications.remove(id);
export const updateNotification = async ({ id, payload }) => EventsNotifications.update(id, payload);
export const testNotification = async ({ id }) => EventsNotifications.testBynotificationId(id);
export const fetchDetectionChainEventDefinitionSummary = async (eventId) => SecurityAppDetectionChains.getSummary(eventId);
export const fetchDetectionChainEvents = async (detectionChainName) => SecurityAppDetectionChains.getEventsForChain(detectionChainName);
